/* ════════════════════════════════════════════════════════════════════════
   MEALS DATA — recipes, weekly plan, shopping list, pantry, leftovers,
   lunchbox ideas. Seed data used by HomeOSStateProvider.
   ════════════════════════════════════════════════════════════════════════ */

export const RECIPE_CATEGORIES = [
  { id: "Dinner",   label: "Dinners",   tone: "rose" },
  { id: "Lunch",    label: "Lunches",   tone: "amber" },
  { id: "Breakfast",label: "Breakfast", tone: "orange" },
  { id: "Lunchbox", label: "Lunchbox",  tone: "yellow" },
  { id: "Baking",   label: "Baking",    tone: "pink" },
  { id: "Snack",    label: "Snacks",    tone: "emerald" },
  { id: "Sauce",    label: "Sauces",    tone: "violet" },
  { id: "Side",     label: "Sides",     tone: "teal" },
];

export const RECIPE_TAGS = [
  "Family Favourite", "Quick", "Budget", "Freezer Friendly",
  "Kid Friendly", "High Protein", "Gluten Free", "Dairy Free",
  "Slow Cooker", "One Pot", "Vegetarian", "Comfort",
];

export const SHOPPING_CATEGORIES = [
  "Meat", "Dairy", "Produce", "Pantry", "Frozen", "Bakery", "Drinks", "Household", "Other",
];

export const PANTRY_STATUSES = ["Good", "Low", "Use soon", "Expired"];
export const LEFTOVER_LOCATIONS = ["Fridge", "Freezer", "Pantry"];
export const DIFFICULTIES = ["Easy", "Medium", "Advanced"];

/* Family Recipe Book — pre-seeded categories (empty by default, filled by
   residents over time). Each entry defines how a recipe matches:
   - `category` matches recipe.category
   - `tag`      matches an entry in recipe.tags
   - `favourite` matches recipe.favourite */
export const RECIPE_BOOK_CATEGORIES = [
  { id: "favourites",       label: "Family Favourites", subtitle: "Recipes worth keeping.",       favourite: true },
  { id: "weeknight",        label: "Weeknight Dinners", subtitle: "Fast dinners on busy nights.", category: "Dinner", tag: "Quick" },
  { id: "slow-cooker",      label: "Slow Cooker",       subtitle: "Set-and-forget suppers.",     tag: "Slow Cooker" },
  { id: "lunchbox",         label: "Lunchbox",          subtitle: "Easy lunches for school.",     category: "Lunchbox" },
  { id: "bbq",              label: "BBQ",               subtitle: "For the deck and long evenings.", tag: "BBQ" },
  { id: "vegetarian",       label: "Vegetarian",        subtitle: "Meat-free meals.",             tag: "Vegetarian" },
  { id: "freezer",          label: "Freezer Meals",     subtitle: "Cook once, eat twice.",        tag: "Freezer Friendly" },
  { id: "baking",           label: "Baking",            subtitle: "Bread, cakes and muffins.",    category: "Baking" },
  { id: "desserts",         label: "Desserts",          subtitle: "Sweet, when the day earned it.", tag: "Dessert" },
  { id: "sauces",           label: "Sauces",            subtitle: "Dressings and condiments.",    category: "Sauce" },
  { id: "breakfast",        label: "Breakfast",         subtitle: "Slow mornings.",               category: "Breakfast" },
  { id: "snacks",           label: "Snacks",            subtitle: "Small things between meals.",  category: "Snack" },
];

/* Match a recipe against a category entry above. */
export function matchesRecipeCategory(recipe, cat) {
  if (!cat || !recipe) return false;
  if (cat.favourite) return !!recipe.favourite;
  if (cat.category && recipe.category === cat.category) return true;
  if (cat.tag && Array.isArray(recipe.tags) && recipe.tags.includes(cat.tag)) return true;
  return false;
}

const ISO_NOW = new Date().toISOString();

export const SEED_RECIPES = [
  {
    id: "r-butter-chicken",
    title: "Butter Chicken",
    subtitle: "Creamy, mild and a family favourite.",
    imageUrl: "",
    imageEmoji: "🍛",
    category: "Dinner",
    tags: ["Family Favourite", "Kid Friendly", "Comfort"],
    prepTime: 15, cookTime: 25, serves: 4, difficulty: "Easy",
    ingredients: [
      "500g chicken thighs, diced", "2 tbsp butter", "1 onion, finely chopped",
      "2 cloves garlic, minced", "1 tsp ginger, grated", "2 tbsp tomato paste",
      "1 cup cream", "2 tsp garam masala", "1 tsp smoked paprika",
      "Salt & pepper", "Fresh coriander, to serve", "Rice + naan, to serve",
    ],
    instructions: [
      "Sauté onion in butter until soft, ~5 min.",
      "Add garlic, ginger, garam masala and paprika. Cook 1 min.",
      "Stir in tomato paste, then chicken. Brown all over.",
      "Pour in cream, simmer 15 min until chicken is cooked through.",
      "Season, top with coriander, serve with rice and naan.",
    ],
    notes: "Double the sauce and freeze half for a fast dinner next week.",
    source: "Family recipe",
    favourite: true, assignedDefaultCook: "dad",
    createdAt: ISO_NOW, updatedAt: ISO_NOW,
  },
  {
    id: "r-spag-bol",
    title: "Spaghetti Bolognese",
    subtitle: "The kids' Wednesday classic.",
    imageUrl: "", imageEmoji: "🍝",
    category: "Dinner",
    tags: ["Family Favourite", "Budget", "Kid Friendly", "One Pot"],
    prepTime: 10, cookTime: 30, serves: 5, difficulty: "Easy",
    ingredients: [
      "500g beef mince", "1 onion, diced", "2 carrots, grated",
      "2 cloves garlic", "2 tbsp tomato paste", "1 can chopped tomatoes",
      "1 cup beef stock", "1 tsp oregano", "Salt & pepper",
      "400g spaghetti", "Parmesan, to serve",
    ],
    instructions: [
      "Brown mince in a hot pan. Drain excess fat.",
      "Add onion, carrot and garlic. Cook 5 min until soft.",
      "Stir in tomato paste, tomatoes, stock and oregano. Simmer 20 min.",
      "Cook spaghetti as per packet. Drain.",
      "Serve with sauce, topped with parmesan.",
    ],
    notes: "Sneak grated zucchini in for extra veg — no one notices.",
    source: "Family recipe",
    favourite: true, assignedDefaultCook: "mum",
    createdAt: ISO_NOW, updatedAt: ISO_NOW,
  },
  {
    id: "r-fish-tacos",
    title: "Fish Tacos",
    subtitle: "Fresh, fast, summery dinner.",
    imageUrl: "", imageEmoji: "🌮",
    category: "Dinner",
    tags: ["Quick", "High Protein", "Family Favourite"],
    prepTime: 15, cookTime: 10, serves: 4, difficulty: "Easy",
    ingredients: [
      "4 white fish fillets", "1 tsp smoked paprika", "1 tsp cumin",
      "Olive oil", "8 small tortillas", "1 avocado, sliced",
      "1 cup red cabbage, shredded", "Lime wedges", "Sour cream",
    ],
    instructions: [
      "Mix paprika, cumin, salt and pepper. Rub onto fish.",
      "Pan-fry fish in olive oil ~3 min each side until flaky.",
      "Warm tortillas. Flake fish into pieces.",
      "Assemble: fish, cabbage, avocado, sour cream, lime.",
    ],
    notes: "Swap fish for grilled tofu for a vegetarian version.",
    source: "Family recipe",
    favourite: false, assignedDefaultCook: "dad",
    createdAt: ISO_NOW, updatedAt: ISO_NOW,
  },
  {
    id: "r-pizza",
    title: "Homemade Pizza",
    subtitle: "Friday night ritual.",
    imageUrl: "", imageEmoji: "🍕",
    category: "Dinner",
    tags: ["Family Favourite", "Budget", "Kid Friendly"],
    prepTime: 20, cookTime: 12, serves: 4, difficulty: "Easy",
    ingredients: [
      "Pizza bases (or dough)", "1/2 cup pizza sauce", "2 cups mozzarella",
      "Toppings of choice (ham, peppers, mushroom, olives)", "Olive oil", "Fresh basil",
    ],
    instructions: [
      "Preheat oven to 220°C.",
      "Spread sauce thinly on bases.",
      "Top with cheese and chosen toppings.",
      "Bake 10–12 min until cheese is bubbling.",
      "Drizzle olive oil and basil on top.",
    ],
    notes: "Let the kids build their own — make it a family event.",
    source: "Family recipe",
    favourite: true, assignedDefaultCook: "all",
    caloriesPerServe: 620, macros: { protein: 26, carbs: 70, fat: 24 }, nutritionCalculated: true,
    createdAt: ISO_NOW, updatedAt: ISO_NOW,
  },
  {
    id: "r-stir-fry",
    title: "Chicken Stir-Fry",
    subtitle: "Empty-the-fridge weeknight win.",
    imageUrl: "", imageEmoji: "🥡",
    category: "Dinner",
    tags: ["Quick", "Budget", "One Pot", "High Protein"],
    prepTime: 10, cookTime: 15, serves: 4, difficulty: "Easy",
    ingredients: [
      "500g chicken, sliced", "Mixed vegetables (capsicum, broccoli, carrot)",
      "2 tbsp soy sauce", "1 tbsp oyster sauce", "1 tsp honey", "1 tsp ginger",
      "2 cloves garlic", "Rice or noodles to serve",
    ],
    instructions: [
      "Cook rice or noodles.",
      "Fry chicken in hot oil until golden.",
      "Add garlic, ginger and vegetables. Stir-fry 4 min.",
      "Add soy, oyster sauce and honey. Toss.",
      "Serve over rice or noodles.",
    ],
    notes: "Great way to use vegetables before grocery day.",
    source: "Family recipe",
    favourite: false, assignedDefaultCook: "mum",
    caloriesPerServe: 480, macros: { protein: 38, carbs: 52, fat: 12 }, nutritionCalculated: true,
    createdAt: ISO_NOW, updatedAt: ISO_NOW,
  },
  {
    id: "r-banana-bread",
    title: "Banana Bread",
    subtitle: "Saves three lonely bananas.",
    imageUrl: "", imageEmoji: "🍌",
    category: "Baking",
    tags: ["Budget", "Kid Friendly", "Family Favourite"],
    prepTime: 10, cookTime: 50, serves: 8, difficulty: "Easy",
    ingredients: [
      "3 ripe bananas, mashed", "1/2 cup melted butter", "3/4 cup sugar",
      "1 egg", "1 tsp vanilla", "1 tsp baking soda", "Pinch of salt",
      "1.5 cups flour",
    ],
    instructions: [
      "Preheat oven to 180°C. Line a loaf tin.",
      "Mix bananas, butter, sugar, egg and vanilla.",
      "Stir in baking soda, salt, then flour.",
      "Pour into tin. Bake 50 min until skewer comes out clean.",
      "Cool before slicing.",
    ],
    notes: "Add chocolate chips for a treat.",
    source: "Nana's recipe",
    favourite: true, assignedDefaultCook: "teen",
    createdAt: ISO_NOW, updatedAt: ISO_NOW,
  },
  {
    id: "r-bento-wraps",
    title: "Wrap + Cheese + Fruit Lunchbox",
    subtitle: "A 3-minute lunchbox build.",
    imageUrl: "", imageEmoji: "🥪",
    category: "Lunchbox",
    tags: ["Quick", "Kid Friendly", "Budget"],
    prepTime: 5, cookTime: 0, serves: 1, difficulty: "Easy",
    ingredients: [
      "1 wholegrain wrap", "Slice of cheese", "Slice of ham or chicken",
      "Cucumber sticks", "Apple slices", "Yoghurt pouch",
    ],
    instructions: [
      "Roll wrap with cheese + protein.",
      "Slice into pinwheels.",
      "Pack with cucumber, apple and yoghurt.",
    ],
    notes: "Use bento dividers to keep dry food crisp.",
    source: "Family routine",
    favourite: false, assignedDefaultCook: null,
    createdAt: ISO_NOW, updatedAt: ISO_NOW,
  },
  {
    id: "r-tomato-sauce",
    title: "All-Purpose Tomato Sauce",
    subtitle: "Pasta, pizza, dipping — one batch does it all.",
    imageUrl: "", imageEmoji: "🍅",
    category: "Sauce",
    tags: ["Budget", "Freezer Friendly", "Vegetarian"],
    prepTime: 5, cookTime: 25, serves: 6, difficulty: "Easy",
    ingredients: [
      "2 cans chopped tomatoes", "2 cloves garlic", "1 tbsp olive oil",
      "1 tsp sugar", "1 tsp dried basil", "Salt & pepper",
    ],
    instructions: [
      "Sauté garlic in olive oil briefly.",
      "Add tomatoes, sugar, basil. Simmer 25 min.",
      "Season and blitz smooth if preferred.",
    ],
    notes: "Freeze in 1-cup portions for quick weeknight dinners.",
    source: "Family recipe",
    favourite: false, assignedDefaultCook: null,
    createdAt: ISO_NOW, updatedAt: ISO_NOW,
  },
];

/* ── Default weekly plan — Mon-Sun ───────────────────────────────── */

function todayMondayIso() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isoFromOffset(offset) {
  const d = todayMondayIso();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

export const SEED_MEAL_PLAN = [
  { day: "Mon", iso: isoFromOffset(0), dinner: "Chicken Stir-Fry",      dinnerRecipeId: "r-stir-fry",      breakfast: "Oats + fruit",          lunch: "Wraps",                 assignedCook: "mum",  status: "Planned",        notes: "" },
  { day: "Tue", iso: isoFromOffset(1), dinner: "Spaghetti Bolognese",   dinnerRecipeId: "r-spag-bol",      breakfast: "Toast + eggs",          lunch: "Leftover stir-fry",     assignedCook: "mum",  status: "Planned",        notes: "" },
  { day: "Wed", iso: isoFromOffset(2), dinner: "Fish Tacos",            dinnerRecipeId: "r-fish-tacos",    breakfast: "Yoghurt + granola",     lunch: "Sandwiches",            assignedCook: "dad",  status: "Planned",        notes: "Lily prefers no avocado" },
  { day: "Thu", iso: isoFromOffset(3), dinner: "Butter Chicken",        dinnerRecipeId: "r-butter-chicken",breakfast: "Smoothies",             lunch: "Rice bowls",            assignedCook: "dad",  status: "Planned",        notes: "" },
  { day: "Fri", iso: isoFromOffset(4), dinner: "Homemade Pizza",        dinnerRecipeId: "r-pizza",         breakfast: "Pancakes",              lunch: "Leftover butter chicken", assignedCook: "all", status: "Planned",        notes: "Pizza night!" },
  { day: "Sat", iso: isoFromOffset(5), dinner: "",                      dinnerRecipeId: null,              breakfast: "Family brunch",         lunch: "Picnic",                assignedCook: null,   status: "Needs decision", notes: "Out for the day?" },
  { day: "Sun", iso: isoFromOffset(6), dinner: "Roast + leftovers",     dinnerRecipeId: null,              breakfast: "Toast",                 lunch: "Roast prep",            assignedCook: "mum",  status: "Planned",        notes: "" },
];

/* ── Shopping list, pantry, leftovers, lunchbox seeds ─────────────── */

export const SEED_SHOPPING_LIST = [
  { id: "s-1", item: "Chicken breast",  category: "Meat",     quantity: "1kg",   checked: false, sourceRecipeId: "r-stir-fry" },
  { id: "s-2", item: "Beef mince",      category: "Meat",     quantity: "500g",  checked: false, sourceRecipeId: "r-spag-bol" },
  { id: "s-3", item: "White fish",      category: "Meat",     quantity: "4 fillets", checked: false, sourceRecipeId: "r-fish-tacos" },
  { id: "s-4", item: "Milk",            category: "Dairy",    quantity: "2L",    checked: true,  sourceRecipeId: null },
  { id: "s-5", item: "Cream",           category: "Dairy",    quantity: "300ml", checked: false, sourceRecipeId: "r-butter-chicken" },
  { id: "s-6", item: "Mozzarella",      category: "Dairy",    quantity: "500g",  checked: false, sourceRecipeId: "r-pizza" },
  { id: "s-7", item: "Bananas",         category: "Produce",  quantity: "6",     checked: false, sourceRecipeId: null },
  { id: "s-8", item: "Red cabbage",     category: "Produce",  quantity: "1",     checked: false, sourceRecipeId: "r-fish-tacos" },
  { id: "s-9", item: "Avocado",         category: "Produce",  quantity: "2",     checked: false, sourceRecipeId: "r-fish-tacos" },
  { id: "s-10",item: "Pasta",           category: "Pantry",   quantity: "400g",  checked: false, sourceRecipeId: "r-spag-bol" },
  { id: "s-11",item: "Tortillas",       category: "Bakery",   quantity: "8",     checked: false, sourceRecipeId: "r-fish-tacos" },
];

export const SEED_PANTRY = [
  { id: "p-1", item: "Rice",             category: "Pantry", quantity: "2kg",   status: "Good",     notes: "" },
  { id: "p-2", item: "Olive oil",        category: "Pantry", quantity: "Half",  status: "Low",      notes: "" },
  { id: "p-3", item: "Canned tomatoes",  category: "Pantry", quantity: "4 cans",status: "Good",     notes: "" },
  { id: "p-4", item: "Soy sauce",        category: "Pantry", quantity: "Half",  status: "Use soon", notes: "" },
  { id: "p-5", item: "Spaghetti",        category: "Pantry", quantity: "500g",  status: "Good",     notes: "" },
];

export const SEED_LEFTOVERS = [
  { id: "lo-1", item: "Roast chicken",  quantity: "300g", location: "Fridge", useBy: isoFromOffset(2),  suggestedUse: "Lunchbox sandwiches or chicken fried rice", status: "Use soon" },
  { id: "lo-2", item: "Cooked rice",    quantity: "2 cups", location: "Fridge", useBy: isoFromOffset(1), suggestedUse: "Fried rice tonight",                          status: "Use soon" },
];

export const SEED_LUNCHBOX_IDEAS = [
  { id: "lb-1", title: "Wraps + cheese + fruit",      ingredients: ["Wrap", "Cheese", "Apple"],                    tags: ["Quick", "Kid Friendly"], assignedTo: null, notes: "" },
  { id: "lb-2", title: "Rice balls + edamame",        ingredients: ["Rice balls", "Edamame", "Mandarin"],          tags: ["Family Favourite"], assignedTo: null, notes: "" },
  { id: "lb-3", title: "Pasta salad + veg sticks",    ingredients: ["Cooked pasta", "Pesto", "Cucumber", "Cherry tomato"], tags: ["Budget"], assignedTo: null, notes: "" },
  { id: "lb-4", title: "Bagel + cream cheese + ham",  ingredients: ["Mini bagel", "Cream cheese", "Ham", "Grapes"], tags: ["Quick"], assignedTo: null, notes: "" },
];
